import { ComplaintModal } from "@complaints/ComplaintModal";
import {
  GO_TO_SHORTCUTS,
  type GO_TO_TARGETS,
  type GoToTarget,
  type ShortcutSurface,
} from "@components/keyboardShortcuts";
import { KeyboardShortcutsModal } from "@components/KeyboardShortcutsModal";
import { useCompanionShortcuts } from "@components/useCompanionShortcuts";
import { useUser } from "@context/UserContext";
import { CorrectionBubble } from "@corrections/CorrectionBubble";
import { CorrectionModal } from "@corrections/CorrectionModal";
import { useBrianBubble } from "@shell/brianBubble";
import { useNavigate } from "@tanstack/react-router";
import { canAccessAdmin } from "@users/userClient";
import { useCallback, useEffect, useState } from "react";
import { flushSync } from "react-dom";

const GO_TO_PATHS: {
  [S in ShortcutSurface]: Record<(typeof GO_TO_TARGETS)[S][number], string>;
} = {
  workstation: {
    schedule: "/schedule",
    board: "/board",
    calls: "/calls",
    email: "/email",
    todos: "/todos",
    settings: "/settings",
  },
  frontDesk: {
    desk: "/display/front-desk",
    schedule: "/display/front-desk/schedule",
    calls: "/display/front-desk/calls",
    email: "/display/front-desk/email",
  },
};

export const GlobalControls = ({
  surface,
  onSearch,
  shortcutsOpen,
  setShortcutsOpen,
}: {
  surface: ShortcutSurface;
  onSearch: () => void;
  shortcutsOpen: boolean;
  setShortcutsOpen: (open: boolean) => void;
}) => {
  const navigate = useNavigate();
  const { currentUser, switchUser } = useUser();
  const [complaintOpen, setComplaintOpen] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const bubble = useBrianBubble();

  const goTo = useCallback(
    (target: GoToTarget) => {
      const paths: Partial<Record<GoToTarget, string>> = GO_TO_PATHS[surface];
      const path = paths[target];
      if (!path) return;
      const shortcut = GO_TO_SHORTCUTS.find((s) => s.target === target);
      if (shortcut?.adminOnly && !canAccessAdmin(currentUser)) return;
      setShortcutsOpen(false);
      void navigate({ to: path });
    },
    [currentUser, navigate, setShortcutsOpen, surface],
  );

  const search = () => {
    // Closing a modal <dialog> restores focus to its opener, so it must close before search takes focus.
    if (shortcutsOpen) flushSync(() => setShortcutsOpen(false));
    onSearch();
  };

  const correct = () => {
    // Same as search: the shortcuts modal must close first or it steals focus back on close.
    if (shortcutsOpen) flushSync(() => setShortcutsOpen(false));
    setCorrectionOpen(true);
  };

  useCompanionShortcuts({
    onSearch: search,
    onHelp: () => setShortcutsOpen(!shortcutsOpen),
    onCorrect: correct,
    onGoTo: goTo,
    onSwitchUser: switchUser,
  });

  useEffect(() => {
    // TODO(BE-1951): TEMPORARY, remove with src/complaints/. Shift because macOS swallows plain ⌘H.
    const onKeyDown = (e: KeyboardEvent) => {
      if (
        e.key.toLowerCase() === "h" &&
        e.shiftKey &&
        (e.metaKey || e.ctrlKey)
      ) {
        e.preventDefault();
        setComplaintOpen(true);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <>
      <ComplaintModal
        isOpen={complaintOpen}
        onClose={() => setComplaintOpen(false)}
      />
      <CorrectionModal
        isOpen={correctionOpen}
        onClose={() => setCorrectionOpen(false)}
        onSent={(res, text, sentAt) =>
          bubble.show(
            <CorrectionBubble
              initial={res}
              text={text}
              sentAt={sentAt}
              onDismiss={bubble.hide}
            />,
          )
        }
      />
      <KeyboardShortcutsModal
        surface={surface}
        isOpen={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
      />
    </>
  );
};
